import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/api';

export interface CommitteeDashboardStats {
  evaluadoresActivos?: number;
  invitacionesPendientes?: number;
  invitacionesAceptadas?: number;
  totalMiembros?: number;
  sinAsignar?: number;
  enRevision?: {
    total?: number;
    ronda1?: number;
    ronda2?: number;
  };
  finalizadas?: number;
  totalPonencias?: number;
  evaluadorCarga?: Array<{
    name: string;
    count: number;
    max: number;
  }>;
}

export interface CommitteeMemberItem {
  id: string;
  user: {
    id: string;
    name: string | null;
    displayName: string;
    email: string;
    authProvider: 'LDAP' | 'LOCAL';
  };
  status: 'PENDING' | 'ACTIVE' | 'REVOKED';
  invitedAt: string;
  joinedAt?: string;
  revokedAt?: string;
}

export interface CommitteeInviteItem {
  id: string;
  email: string;
  status: 'PENDING' | 'ACCEPTED' | 'EXPIRED' | 'CANCELLED';
  expiresAt: string;
  createdAt: string;
  usedAt?: string;
}

async function fetchCommitteeDashboardStats(eventId: string): Promise<CommitteeDashboardStats> {
  const { data } = await api.get('/committee/dashboard/stats', { params: { eventId } });
  return data as CommitteeDashboardStats;
}

async function fetchCommitteeMembers(eventId: string): Promise<CommitteeMemberItem[]> {
  const { data } = await api.get('/committee/members', { params: { eventId } });
  if (!Array.isArray(data?.members)) {
    return [];
  }
  return data.members as CommitteeMemberItem[];
}

async function fetchCommitteeInvites(eventId: string): Promise<CommitteeInviteItem[]> {
  const { data } = await api.get('/committee/invites', { params: { eventId } });
  if (!Array.isArray(data?.invites)) {
    return [];
  }
  return data.invites as CommitteeInviteItem[];
}

export function useCommitteeDashboardStats(eventId: string) {
  return useQuery({
    queryKey: ['committee-dashboard', eventId],
    queryFn: () => fetchCommitteeDashboardStats(eventId),
    enabled: Boolean(eventId),
    staleTime: 60_000,
  });
}

export function useCommitteeMembers(eventId: string) {
  return useQuery({
    queryKey: ['committee-members', eventId],
    queryFn: () => fetchCommitteeMembers(eventId),
    enabled: Boolean(eventId),
    staleTime: 30_000,
  });
}

export function useCommitteeInvites(eventId: string) {
  return useQuery({
    queryKey: ['committee-invites', eventId],
    queryFn: () => fetchCommitteeInvites(eventId),
    enabled: Boolean(eventId),
    staleTime: 30_000,
  });
}

export function useCreateInvite(eventId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: { email: string; message?: string }) =>
      api.post('/committee/invites', { ...payload, eventId }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['committee-members', eventId] }),
        queryClient.invalidateQueries({ queryKey: ['committee-invites', eventId] }),
        queryClient.invalidateQueries({ queryKey: ['committee-dashboard', eventId] }),
      ]);
    },
  });
}

export function useRevokeMember(eventId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ memberId, confirmation }: { memberId: string; confirmation: boolean }) =>
      api.post(`/committee/members/${memberId}/revoke`, { confirmation }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['committee-members', eventId] }),
        queryClient.invalidateQueries({ queryKey: ['committee-dashboard', eventId] }),
      ]);
    },
  });
}

export function useResendInvite(eventId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (inviteId: string) => api.post(`/committee/invites/${inviteId}/resend`),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['committee-invites', eventId] }),
        queryClient.invalidateQueries({ queryKey: ['committee-members', eventId] }),
        queryClient.invalidateQueries({ queryKey: ['committee-dashboard', eventId] }),
      ]);
    },
  });
}

